import { FeatureFlags } from "@packages/feature-flags";
import { Product, ProductListConfig, ResultsInfo } from "./types/types";
import { layoutMap as listLayouts } from "./product-list-layouts/layoutMap";
import { raise } from "./utils/raise";
import { useSocialProofMessaging } from "./product-card-components/SocialProofMessaging/useSocialProofMessaging";
import { SocialProofMessagesProvider } from "./contexts/SocialProofMessagesContext/SocialProofMessagesContext";
import { FeatureFlagsProvider } from "./flags/flags";
import { useVisibleProducts } from "./utils/useVisibleProducts";
import { Ref, useEffect, useRef } from "react";
import { createProductListEventDispatcher } from "./utils/events/dispatchEvent";
import { PRODUCT_LIST_READY } from "./utils/events/productListEvents";
import { ProductListEventContext } from "./utils/events/useDispatchEvent";

type ProductListProps = {
  products: Product[];
  config: ProductListConfig;
  experiencesRawString?: string;
  resultsInfo: ResultsInfo;
  heading?: {
    main: string;
    subHeading: string | null;
  };
  flags: FeatureFlags;
  instanceId?: string;
};

export function ProductList({
  products,
  config,
  experiencesRawString,
  resultsInfo,
  heading,
  flags,
  instanceId,
}: ProductListProps) {
  const ref = useRef<HTMLElement | null>(null);
  const dispatch = createProductListEventDispatcher(ref);

  const visibleProducts = useVisibleProducts(products, flags);

  const { name: listLayoutName, props: listLayoutProps } =
    config["product-list-layout"];

  const ListLayout =
    listLayouts[listLayoutName] ||
    raise(`Couldn't find product-list layout "${listLayoutName}"`);

  const socialProofComponent = (
    config["product-card-slots"].banner ||
    config["product-card-slots"]["image-banner"]
  )?.filter((bslot) => bslot.name === "SocialProofMessaging");

  const socialProofMessages = useSocialProofMessaging({
    resultsInfo,
    products: visibleProducts,
    appId: `${(socialProofComponent && socialProofComponent[0]?.props?.appId) || ""}`,
  });

  useEffect(() => {
    if (ref.current) {
      dispatch({
        type: PRODUCT_LIST_READY,
        instanceId: instanceId || "",
      });
    }
  }, []);

  return (
    <FeatureFlagsProvider
      flags={flags}
      experiencesRawString={experiencesRawString}
      id="product-list"
    >
      <SocialProofMessagesProvider messages={socialProofMessages}>
        <ProductListEventContext.Provider value={dispatch}>
          <template
            ref={ref as Ref<HTMLTemplateElement>}
            data-id="product-list-event-emitter"
          />
          <ListLayout
            config={config}
            products={visibleProducts}
            configProps={listLayoutProps}
            resultsInfo={resultsInfo}
            heading={heading}
          />
        </ProductListEventContext.Provider>
      </SocialProofMessagesProvider>
    </FeatureFlagsProvider>
  );
}
